import {
  HtmlSerializer,
  tools as EditorTools,
  AhaEditorUtils,
  type AhaSlateEditor,
  slate,
} from '@aha-app/aha-editor-v2';
import React from 'react';
import ReactDOMServer from 'react-dom/server.browser';
import '../editorStyles.css';
import editorCss from 'inline:../editorStyles.css';

import classnames from 'classnames';
import EditorV2Container from './EditorV2Container';
import {
  stripEmptyHTML,
  regularSanitizer,
  simpleSanitizer,
  getPaddingCssFormat,
  plainText,
} from '../utils/html';
import {
  verticalAlignToFlex,
  normalizeVerticalAlign,
} from '../utils/verticalAlign';
import { fabric } from 'fabric';
import { type HasEmbeddedText, type Constructor, type Rect } from '../types';
import tools from '../tools';
import type ShapeBase from '../shapes/ShapeBase';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import type TextField from './TextField';
import { type ToolList } from '../fabric/ObjectBehaviors';
import { applyTextColor } from '../tools/ToolTextColor';
import { getBoundingBoxFromLocalRect, pointInRect } from '../utils/geometry';
import { type IPoint } from 'fabric/fabric-impl';
import getMeasurementDoc from '../utils/measurement';
import { createRoot, type Root } from 'react-dom/client';
import { hasDataStoreRecordBehaviors, isTable, isTextField } from './guards';

const { Editor, createEditor, Element: SlateElement } = slate;

// The minimum width of an autogrow field.
const AUTO_GROW_MIN_WIDTH = 50;

// The default font size.
const DEFAULT_FONT_SIZE = 14;

// Properties that may require the text image to be re-generated.
const TEXT_PROPERTIES = [
  'shapeName',
  'textAlign',
  'verticalAlign',
  'fontSize',
  'textLocation',
  'textMargin',
  'scaleX',
  'scaleY',
  'strokeWidth',
];

// Safari handles Image objects created from SVGs differently than Chrome
// and Firefox. Chrome and Firefox generate an Image that is a vector
// representation and will scale infinitely. Safari renders the SVG to a
// bitmap in the Image. This is a problem because when the canvas is zoomed
// in, the text becomes pixelated and blurry. The solution is render the
// text at 2x the resolution.
const IMAGE_SCALE = 2;

// Variables copied from aha-base since external CSS is not visible inside a foreignObject.
const EDITOR_CSS_VARIABLES = `
      :root {
        --theme-link-text: rgb(0, 115, 207);
        --theme-primary-border: rgb(205, 205, 205);
        --theme-secondary-border: rgb(205, 205, 205);
        --theme-light-border: rgb(241, 241, 241);
        --aha-yellow-400: rgb(255, 228, 142);
        --aha-gray-700: rgb(150, 153, 153);

        --aha-orange-600: rgb(249, 147, 25);
        --aha-orange-800: rgb(199, 109, 0);
        --aha-red-600: rgb(235, 121, 87);
        --aha-blue-500: rgb(0, 115, 207);
        --theme-green-text: rgb(79, 143, 14);
        --default-type-margin: 1em;
      }
      `;

// Delay before the resize observed element is removed from the DOM
const CLEANUP_DELAY = 4000;

/* Styles for rendered content which can't also be applied to the editor,
   since (for e.g.) they clash with required styles for editing */
const extraStyles = `
  hr {
    border-style: none;
    margin: 20px 0;
    border-top: 1px solid var(--theme-secondary-border);
  }

  .aha-editor-v2__li--hide-marker {
    list-style-type: none;
  }
`;

const renderedTextStyles = `
  ${EDITOR_CSS_VARIABLES}
  ${editorCss}
  ${extraStyles}
`;

export interface TextDimensions {
  width: number;
  height: number;
}

type TextAlign = 'user' | 'shape' | 'left' | 'center' | 'right';
export type EditorAlign = 'left' | 'right' | 'center';
export type EditorVerticalAlign = 'top' | 'middle' | 'bottom';
type ListType = 'ol_list' | 'ul_list' | 'checklist';
interface RawHoveredLink {
  rects: Rect[];
  href: string;
}

// track the different text marks e.g. bold, italic, etc.
export const TEXT_MARKS = [
  'bold',
  'italic',
  'underline',
  'strikethrough',
  'code',
] as const;
type TextMarksType = {
  [key in (typeof TEXT_MARKS)[number]]: boolean;
};
export interface TextFieldProps extends TextMarksType {
  textAlign: EditorAlign;
  verticalAlign: EditorVerticalAlign;
  fontSize: number;
  textOpacity: number | null;
  textColor: string;
}

export type WrapTextType =
  | 'user'
  | 'wrap'
  | 'growObject'
  | 'growText'
  | 'wrapGrow'
  | 'wrapFixed';

interface TextProps {
  /**
   * Styling allowed for the text.
   * - `simple` - only allow simple text formatting.
   * - `any` - allow any formatting that aha-editor is capable of.
   */
  allowedStyles: 'simple' | 'any';
  /**
   * Where the text is located relative to the object.
   * - `user` - use the value from the `textLocation` property.
   */
  location: 'user' | 'inside' | 'above' | 'below' | 'left' | 'right';
  /**
   * Whether the text alignment is forced to a value, or deferred to text property.
   * - `user` - use the value from the `textAlign` property.
   * - `shape` - use the value from the `shapeTextAlign()` method.
   */
  textAlign: TextAlign;
  /**
   * Similar to textAlign, but for vertical text alignment. Like css vertical-align.
   * verticalAlign can also be set to none to disallow vertical align options in the UI.
   */
  verticalAlign: 'user' | 'shape' | 'top' | 'middle' | 'bottom' | 'none';
  /**
   * How the fontSize relates to the object's scale.
   * - `user` - use the value from the `fontSize` property.
   * - `scaleToFit` - scale the font size to the maximum to fit the text in the object.
   * - `fixed` - use a fixed value for the shape regardless of scale.
   */
  fontSize: 'user' | 'scaleToFit' | 'fixed';
  /** How overflow text is handled. */
  overflow: 'clip' | 'visible';
  /**
   * Whether the text should wrap to the object or grow the object.
   * - `user` - use the value from the `wrapText` property or growObject.
   * - `wrap` - wrap the text to the object.
   * - `growObject` - grow the object to fit the text.
   * - `growText` - grow the text dimensions to fit the text, but not the object.
   * - `wrapGrow` - combines the behaviors of wrap and growText.
   *   - Object grows with text, but does not shrink with text based on object.minHeight
   *   - Uses position of wrap
   */
  wrapText: WrapTextType;
  /**
   * How margins will be treated.
   * - `scale` - scale the margins with the object.
   * - `noScale` - don't scale the margins with the object.
   */
  margins: 'scale' | 'noScale';
  /**
   * Whether pressing Enter (without Shift) ends editing.
   * - When true, Enter confirms changes and Shift+Enter adds newlines.
   * - When false or undefined, Enter adds newlines (default behavior).
   */
  enterKeyEndsEditing?: boolean;
}

// Interface for
export interface EmbeddedTextFormatQueryable {
  selectionIsEntirely(
    textFormat:
      | 'bold'
      | 'italic'
      | 'code'
      | 'strikethrough'
      | 'underline'
      | string
  ): boolean;
}

// Behaviors to add to objects which have embedded text.
//
// There are three general types of behavior depending on the object and the
// action. These behaviors are controlled by the `textProps` property.
//
// Default:
//   * Font size is user controlled and doesn't change.
//   * Text wraps to the container and is cropped when not editing.
// Autosize: (used by ShapeNote)
//   * Font size is dynamically updated to ensure the text is all visible at
//     the largest possible size.
// Autogrow: (used by ShapeText)
//   * The object grows and shrinks to exactly fit the text.
//   * The font size is proportional to the scale, so that resizing the object
//     increases the font size.
//   * If the user manually changes the width then `wrapText` is set and from
//     then on, only the height grows dynamically.
//
// When resized in a group all objects have Auto-grow behavior so that the font
// size updates. This is to make sure that text continues to look the same
// after resizing.

// Interface declares public properties added by the decorator
interface EmbeddedTextBehaviors extends EmbeddedTextFormatQueryable {
  textDimensions: TextDimensions;
  textContent: string;
  textContentIsEmpty(): boolean;
  textImage: HTMLImageElement | null;
  textProps: TextProps;
  textEditing: boolean;
  _textFields: { [key: string]: TextField };
  editor: AhaSlateEditor;
  activeTextField: TextField | this | null;
  minWidth: number;
  minHeight: number;
  addText(content: string): void;
  addTextField(textField: TextField): void;
  removeTextField(textField: TextField): void;
  get hasTextFields(): boolean;
  initializeText(): Promise<void>;
  setFromRemote(key: KeyPath, value: any, options?: Record<string, any>): void;
  defaultContent(): string;
  setTextContent(content: string, didEndEditing?: boolean): Promise<void>;
  syncTextImage(): void;
  endTextEditing(): void;
  textPosition(): { left: number; top: number; width: number; height: number };
  renderText(ctx: CanvasRenderingContext2D, onlyFields?: boolean): void;
  sanitizeTextContent(): string;
  sanitizedTextContent(): string;
  sanitizedTextAlign(): EditorAlign;
  sanitizedVerticalAlign(): EditorVerticalAlign;
  measureTextDimensions(size?: unknown): Promise<{
    width: number;
    height: number;
  }>;
  sizeTextToFit(): Promise<void>;
  scaledTextMargin(): {
    left: number;
    top: number;
    right: number;
    bottom: number;
  };
  growText(widthDiff: number): { left: number; top: number };
  createOffscreenEditor(): AhaSlateEditor;
  getCurrentEditor(): AhaSlateEditor;
  shrinkText(): Promise<number>;
  applyTextProperty(
    applyFunc: (editor: AhaSlateEditor, obj: fabric.Object) => void,
    operateOnAll?: boolean
  ): void;
  applyDefaultProperties({
    force,
    operateOnAll,
  }: {
    force?: boolean;
    operateOnAll?: boolean;
  }): void;
  readTextProperty<T>(
    readFunc: (editor: AhaSlateEditor, obj: fabric.Object) => T
  ): T;
  hasAnyTextColor(): boolean;
  plainText(): string;
  removeObserver(): void;
  textTools(): ToolList;
  forceResizeTextToFit(): void;
  textDimensionsMeasured: boolean;
  textAlign: EditorAlign;
  verticalAlign: EditorVerticalAlign;
  textColor: string;

  // external so we can handle mouse/hover states in nested objects, which don't receive events
  handleMouseMove: ({
    pointer,
    absolutePointer,
  }: Pick<fabric.IEvent<MouseEvent>, 'pointer' | 'absolutePointer'>) => void;
  handleMouseOut: () => void;
  detectHoveredLinks: (pointer: IPoint) => void;
  setSelectedTextField: (event: fabric.IEvent) => void;
  wrapText?: boolean;
  textClass: () => string | null;
  maybeStartTextEditing: (shouldPreventTextEditing?: boolean) => void;
  startTextEditing(selectAll?: boolean): Promise<void>;
  renderTextToImage(): Promise<null | undefined>;
  growObjectToFit(widthChange?: boolean): void;
  isNewlyCreated?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
function EmbeddedTextBehaviors(
  Base: Constructor<fabric.Object & HasEmbeddedText>
): any {
  return class WithEmbeddedTextBehaviors
    extends Base
    implements EmbeddedTextBehaviors
  {
    __hasEmbeddedTextBehaviors = true;

    textDimensions: TextDimensions;
    textContent: string;
    textImage: HTMLImageElement | null;
    textProps: TextProps;
    textEditing: boolean = false;
    initialized: boolean = false;
    // Track actual text field objects represented by textFields in the doc
    _textFields: { [key: string]: TextField };
    activeTextField: TextField | this | null;
    textDimensionsMeasured: boolean;

    // Used to prevent shrinking when textProps.wrapText === 'wrapGrow'
    minWidth: number;
    minHeight: number;

    _cache: Record<
      string,
      {
        keyString: string;
        value: TextDimensions;
      }
    > = {};

    editor: AhaSlateEditor;
    initialTextContent: string;
    dontRecurseSet: boolean;
    startingFontSize: number | null;
    startingScaleX: number | null;
    parent?: fabric.Object;
    hasEmbeddedText: () => boolean;
    updateTextFieldDimensions?: () => void;
    textOpacity: number | null;
    debugContainer?: HTMLElement;
    linkData?: RawHoveredLink[];
    _observedElements?: Map<unknown, (el, rect) => void> | null;
    observer?: ResizeObserver | null;
    wrapText?: boolean;
    textClass: () => string | null;
    shapeName?: string;
    _sanitizedTextContent?: string;
    shape: ShapeBase;
    isDebouncing?: boolean;
    textColor: string;
    textAlign: EditorAlign;
    verticalAlign: EditorVerticalAlign;
    private hoveredRect: Rect | undefined;
    determineLinkPositionsTimeout?: ReturnType<typeof setTimeout>;
    private resizeObservedElement?: HTMLElement;
    private cleanupResizedElementTimer?: ReturnType<typeof setTimeout>;
    isNewlyCreated?: boolean;

    constructor(...args) {
      super(...args);

      this.on('mousemove', this.handleMouseMove);
      this.on('mousewheel', this.handleMouseMove);
      this.on('mouseout', this.handleMouseOut);

      this.on('keydown', eventData => {
        if (this.textEditing && eventData.e.key === 'Escape') {
          this.endTextEditing();
          this.controller.actionFocus(false);
        }

        return true;
      });
    }

    handleMouseOut() {
      this.clearHoveredLink();
    }

    handleMouseMove({
      e,
      pointer,
    }: Pick<fabric.IEvent<MouseEvent>, 'e' | 'pointer' | 'absolutePointer'>) {
      // don't show link hover state...
      if (
        // if the object has been removed from the canvas
        !this.canvas ||
        // while dragging a selection
        this.canvas?._groupSelector ||
        // while awaiting recalculation of link positions
        !!this.determineLinkPositionsTimeout ||
        // or if hovering a control which sits outside the shape
        (pointer && !this.containsPoint(pointer))
      ) {
        return;
      }

      // getLocalPointer gives a position relative to the shape, accounting for rotation etc
      const localPointer = this.getLocalPointer(e);

      // detect any hovered links in this object, or its child text fields (used by tables)
      this.detectHoveredLinks(localPointer);
      for (const textField of Object.values(this._textFields) ?? []) {
        // textFields are positioned relatively to their parent, so we need to adjust accordingly
        const textFieldPointer = textField.getLocalPointer(e);

        textField.detectHoveredLinks({
          x: textFieldPointer.x - this.left,
          y: textFieldPointer.y - this.top,
        });
      }
    }

    private clearHoveredLink() {
      // clear local hovered link if any
      if (this.hoveredRect) {
        this.hoveredRect = undefined;
        this.controller.actionLinkHovered(undefined);
      }
    }

    detectHoveredLinks(localPointer: IPoint) {
      // Adjust for non-default text field
      const { scaleX, scaleY, strokeWidth } = this.parent ?? this;

      const position = this.relativeTextPosition();

      // if text is scaled to fit (e.g. stickies), need to scale pointer position accordingly
      const xScaleFactor =
        this.textProps.fontSize === 'scaleToFit' ? scaleX : 1;
      const yScaleFactor =
        this.textProps.fontSize === 'scaleToFit' ? scaleY : 1;

      // adjust based on stroke width if that is going to be used to draw a
      // border which impacts text position
      const borderAdjust =
        this.getTextLocation() === 'inside' &&
        ['wrap', 'wrapGrow'].includes(this.textProps.wrapText);
      const borderAdjustment = borderAdjust ? strokeWidth ?? 0 : 0;

      const adjustedPointer = {
        x: localPointer.x / xScaleFactor - borderAdjustment - position.left,
        y: localPointer.y / yScaleFactor - borderAdjustment - position.top,
      };

      let hoveredLink: RawHoveredLink | undefined;
      let hoveredRect: Rect | undefined;
      for (const link of this.linkData ?? []) {
        hoveredRect = link.rects.find(rect =>
          pointInRect(adjustedPointer, rect)
        );
        if (hoveredRect) {
          hoveredLink = link;
          break;
        }
      }

      // save as a class property so we can only call the action when needed
      if (hoveredRect !== this.hoveredRect) {
        if (hoveredRect && hoveredLink) {
          this.hoveredRect = hoveredRect;

          let leftOffset = 0;
          let topOffset = 0;
          // eslint-disable-next-line @typescript-eslint/no-this-alias
          let contextObject: fabric.Object = this;
          if (this.parent) {
            // account for nested positioning for table cells
            leftOffset = this.left;
            topOffset = this.top;
            contextObject = this.parent;
          }

          this.controller.actionLinkHovered({
            href: hoveredLink.href,
            rect: getBoundingBoxFromLocalRect({
              rect: {
                left:
                  leftOffset + position.left + hoveredRect.left * xScaleFactor,
                top: topOffset + position.top + hoveredRect.top * yScaleFactor,
                width: hoveredRect.width * xScaleFactor,
                height: hoveredRect.height * yScaleFactor,
              },
              borderAdjustment,
              object: contextObject,
            }),
          });
        } else {
          this.clearHoveredLink();
        }
      }
    }

    /**
     * Returns true if the selected text, or entire document if no selection, is entirely bold, italic, etc.
     */
    selectionIsEntirely(
      textFormat:
        | 'bold'
        | 'italic'
        | 'code'
        | 'strikethrough'
        | 'underline'
        | string
    ) {
      const objs: (fabric.Object & EmbeddedTextBehaviors)[] = this.hasTextFields
        ? this.selectedTextFields
        : [this];

      return objs.every(obj => {
        const editor = obj.getCurrentEditor();
        if (textFormat === 'code') {
          // Code snippets are different than other types of formatting, they are
          // components instead of marks.
          const codeSnippets = Array.from(
            Editor.nodes(editor, {
              match: element =>
                SlateElement.isElementType(element, 'code-snippet'),
            })
          );
          return codeSnippets.length !== 0;
        } else {
          return !!AhaEditorUtils.Marks.getFirstTextNodeMark(
            editor,
            textFormat
          );
        }
      });
    }

    get isTextBold() {
      return this.selectionIsEntirely('bold');
    }

    set isTextBold(value: boolean) {
      const fn = value ? 'add' : 'remove';
      this.applyTextProperty(editor =>
        AhaEditorUtils.Marks[fn](editor, 'bold')
      );
    }

    get isTextItalic() {
      return this.selectionIsEntirely('italic');
    }

    set isTextItalic(value: boolean) {
      const fn = value ? 'add' : 'remove';
      this.applyTextProperty(editor =>
        AhaEditorUtils.Marks[fn](editor, 'italic')
      );
    }

    get isTextCode() {
      return this.selectionIsEntirely('code');
    }

    set isTextCode(value: boolean) {
      const fn = value ? 'add' : 'remove';
      this.applyTextProperty(editor =>
        AhaEditorUtils.Marks[fn](editor, 'code')
      );
    }

    get isTextStrikethrough() {
      return this.selectionIsEntirely('strikethrough');
    }

    set isTextStrikethrough(value: boolean) {
      const fn = value ? 'add' : 'remove';
      this.applyTextProperty(editor =>
        AhaEditorUtils.Marks[fn](editor, 'strikethrough')
      );
    }

    get isTextUnderline() {
      return this.selectionIsEntirely('underline');
    }

    set isTextUnderline(value: boolean) {
      const fn = value ? 'add' : 'remove';
      this.applyTextProperty(editor =>
        AhaEditorUtils.Marks[fn](editor, 'underline')
      );
    }

    get textListType(): ListType | null {
      const textObjects: (fabric.Object & EmbeddedTextBehaviors)[] = this
        .hasTextFields
        ? this.selectedTextFields
        : [this];

      let listType: ListType | null = null;
      const { List } = AhaEditorUtils;
      for (const o of textObjects) {
        const oListType = o.readTextProperty(editor => {
          return (
            (List.inListOfType(editor, 'ul_list') && 'ul_list') ||
            (List.inListOfType(editor, 'ol_list') && 'ol_list')
          );
        });
        if (!oListType) {
          return null;
        }
        if (!listType) {
          listType = oListType;
        } else if (listType !== oListType) {
          return null;
        }
      }
      return listType;
    }

    set textListType(listType: ListType | null) {
      this.applyTextProperty(editor => {
        const { ListWrap } = AhaEditorUtils.List;
        if (listType) {
          ListWrap.wrapInList(editor, { type: listType });
        } else {
          ListWrap.unwrapList(editor);
        }
      });
    }

    setCurrentTextColor(color: string | null) {
      this.applyTextProperty(editor => {
        AhaEditorUtils.Marks.add(editor, EditorTools.ToolTextColor.mark, color);
      }, false);
    }

    get currentTextColor(): string | null {
      const objs = this.hasTextFields ? this.selectedTextFields : [this];
      let color: string | null = null;
      for (const obj of objs) {
        const editor = obj.getCurrentEditor();
        const newColor = AhaEditorUtils.Marks.getFirstTextNodeMark(
          editor,
          'textColor'
        );

        if (newColor && typeof newColor === 'string') {
          if (!color) {
            color = newColor;
          }
          if (color !== newColor) {
            return null;
          }
        } else {
          if (obj.hasAnyTextColor()) {
            return null;
          }
        }
      }
      return color ?? '#000000';
    }

    set currentTextColor(color: string | null) {
      this.setCurrentTextColor(color);
    }

    hasAnyTextColor(): boolean {
      const editor = this.createOffscreenEditor();
      return AhaEditorUtils.Marks.hasMark(editor, 'textColor');
    }

    // Allow the object to support text.
    addText(content: string) {
      this.textEditing = false;
      this._textFields ||= {};

      void this.setTextContent(content);

      this.on('mousedblclick', () => {
        this.maybeStartTextEditing(this.preventTextEditing?.());
      });

      this.on('mousedown:before', e => {
        if (this.canvas?.controller.allowSelection() && !this.locked) {
          this.setSelectedTextField(e);
        }
      });

      if (this.textProps.wrapText === 'wrapGrow') {
        this.set('wrapText', true);
        this.minHeight = this.height;
        this.minWidth = this.width;
      }
    }

    addTextField(textField: TextField) {
      this._textFields ||= {};
      this._textFields[textField.key] = textField;
    }

    removeTextField(textField: TextField) {
      this.set(['textFields', textField.key], null);
      delete this._textFields[textField.key];
    }

    setSelectedTextField(event: fabric.IEvent) {
      // Only set an activeTextField if this object is already selected
      if (this.controller.selection === this) {
        if (!this.hasTextFields) {
          this.activeTextField = this;
        } else {
          const { absolutePointer } = event;
          const textField = Object.values(this._textFields).find(
            (field: TextField) => {
              const contains = field.containsAbsolutePointer(
                this,
                absolutePointer!
              );
              if (!contains) {
                field.endTextEditing();
              }
              return contains;
            }
          );
          if (this.activeTextField != null) {
            this.activeTextField?.endTextEditing();
          }

          this.activeTextField = textField ?? this;
        }
      } else {
        this.activeTextField = null;
      }
    }

    get hasTextFields() {
      return (
        this._textFields != null && Object.keys(this._textFields).length > 0
      );
    }

    get textFieldsInitialized() {
      if (!this.hasTextFields) {
        return true;
      }

      // We only care about any text field being initialized so that we can
      // update fields properly
      return Object.values(this._textFields).every(
        field => field.textContent == null || field.textDimensionsMeasured
      );
    }

    textFieldDefaultProps() {
      return {
        textAlign: this.textAlign,
        verticalAlign: this.verticalAlign,
        fontSize: this.fontSizeWithDefault(),
        textOpacity: this.textOpacity,
        textColor: this.textColor,
        ...Object.fromEntries(TEXT_MARKS.map(mark => [mark, this[mark]])),
      };
    }

    async initializeText() {
      // Reset the text.
      if (this.textProps.fontSize === 'scaleToFit') {
        await this.sizeTextToFit();
      }

      if (['user', 'growObject'].includes(this.textProps.wrapText)) {
        this.growObjectToFit();
      }
      await this.renderTextToImage();
    }

    async _instantiateTextObject(content: string) {
      if (this.initialized) return;

      this.initialized = true;
      content = content || this.defaultContent();
      this.textContent = content;

      const initialize = () => {
        requestIdleCallback(
          () => {
            const init = () => {
              void this.initializeText();
            };
            // Running this code in a callback may be causing growObjectToFit to
            // sometimes set dimensions outside of the safety net of discardOutgoingOps
            // in Seralization.restoreDocument. Without a repro it is hard to know
            // why this happens, but this seems like the safest workaround until
            // we get a reliable repro.
            if (
              hasDataStoreRecordBehaviors(this) &&
              !this.controller.props.allowUpdate
            ) {
              this.withoutCollaboration(init);
            } else {
              // Asynchronously initializing the text here may add ops to the undo
              // stack, which will leave them orphaned from their "true" undo group
              // and potentially break undo.
              this.controller.undoManager.withoutUndo(() => {
                init();
              });
            }
          },
          { timeout: 1000 }
        );

        this.canvas?.on('pan', () => this.endTextEditing());
        this.canvas?.on('zoom', () => this.endTextEditing());
      };

      this.on('added', initialize);
      if (this.canvas) {
        initialize();
      }

      this.on('deselected', () => this.endTextEditing());
      this.on('moving', () => this.endTextEditing());
      this.on('rotating', () => this.endTextEditing());
      this.on('scaling', () => {
        this.startingFontSize ||= this.fontSizeWithDefault();
        this.startingScaleX ||= this.scaleX;

        this.endTextEditing();
      });
      this.on('scaled', () => {
        this.startingFontSize = null;
        this.startingScaleX = null;
      });

      this.on('scaled', () => {
        if (this.textProps.wrapText === 'wrapFixed') {
          void this.forceRemeasureText();
        }
      });

      this.textDimensions = await this.measureTextDimensions();
      this.textDimensionsMeasured = true;
    }

    // Intercept properties that may be for the text.
    _set(keyOrPath: KeyPath, value: any): fabric.Object {
      super._set(keyOrPath, value);

      if (this.dontRecurseSet) return this;
      if (this.hasTextFields) {
        if (!this.textFieldsInitialized) return this;
      } else if (
        (!this.shape && !this.parent) ||
        !this.textDimensionsMeasured
      ) {
        return this; // Not initialized yet
      }

      if (keyOrPath === 'scaleX') {
        const { startingFontSize, startingScaleX } = this;

        if (['user', 'growObject'].includes(this.textProps.wrapText)) {
          // Note the use of withoutSync here so that we don't renter this
          // function and trigger re-computation again.
          this.withoutRecusion(() => {
            this.set('fontSize', DEFAULT_FONT_SIZE * this.scaleX);
          });
        } else if (
          this.textProps.fontSize !== 'fixed' &&
          this.isGroupedOperation() &&
          startingFontSize
        ) {
          // When an object is grouped change the font-size rather than changing
          // the wrapping.
          this.withoutRecusion(() => {
            this.set(
              'fontSize',
              startingFontSize * (this.scaleX / (startingScaleX as number))
            );
          });
        }
      }

      if (['user', 'growObject'].includes(this.textProps.wrapText)) {
        if (keyOrPath === 'fontSize') {
          this.updateFontSize();
        } else if (keyOrPath === 'textContent' || keyOrPath === 'width') {
          this.growObjectToFit(keyOrPath === 'width');
          this.synchronize();
        }
      }

      if (this.textProps.wrapText === 'wrapGrow') {
        if (
          typeof keyOrPath === 'string' &&
          ['fontSize', 'textContent', 'width'].includes(keyOrPath)
        ) {
          this.growObjectToFit(keyOrPath === 'width');
          this.synchronize();
        }
      }

      if (
        typeof keyOrPath === 'string' &&
        TEXT_PROPERTIES.includes(keyOrPath)
      ) {
        this.syncTextImage();
        if (this.hasTextFields) {
          this.selectedTextFields.forEach(field => field.syncTextImage());
        }
      }

      if (this.hasTextFields) {
        if (
          typeof keyOrPath === 'string' &&
          ['width', 'height'].includes(keyOrPath)
        ) {
          if (typeof this.updateTextFieldDimensions !== 'function') {
            throw new Error(
              'No implementation found for updateTextFieldDimensions'
            );
          }
          this.updateTextFieldDimensions();
        }
      }

      return this;
    }

    // Special handling to keep sync remote changes on textFields
    setFromRemote(key: KeyPath, value: any) {
      super.setFromRemote(key, value);

      if (!Array.isArray(key) || key[0] !== 'textFields' || key.length < 2) {
        return;
      }

      const textField = this._textFields[key[1]];
      if (!textField) {
        return;
      }
      textField.set(key[2], value);
    }

    // The object is part of a group and is not being manipulated directly.
    isGroupedOperation() {
      return this.simpleGroup && this.canvas.getActiveObject() !== this;
    }

    defaultContent() {
      return `<p></p>`;
    }

    async setTextContent(content: string, didEndEditing = false) {
      // We don't re-render the text while editing.
      // if we are not currently editing, either we just finished
      //  editing, or this must be a remote op from a collaborator
      if (this.textEditing) return;

      if (content && content.length > 0) {
        this.sanitizeTextContent();

        if (!this.initialized) {
          // if new, we need to initialize the text object, this will measure and render the text
          await this._instantiateTextObject(content);
        } else {
          // otherwise we just need to update the autosize, unless we just finished
          // doing that while editing, and render the new text
          if (this.textProps.fontSize === 'scaleToFit' && !didEndEditing) {
            await this.sizeTextToFit();
          }

          await this.renderTextToImage();
        }
      } else {
        this.textImage = null;
      }
    }

    getTextContent() {
      return this.textContent;
    }

    textContentIsEmpty() {
      if (!this.textContent || this.textContent.length === 0) return true;

      return false;
    }

    // Keep text position in sync with its parent.
    syncTextImage() {
      if (this.textProps.fontSize !== 'scaleToFit') {
        void this.renderTextToImage();
      }
    }

    withoutRecusion(func: () => void) {
      try {
        this.dontRecurseSet = true;
        func();
      } finally {
        this.dontRecurseSet = false;
      }
    }

    containerElement() {
      return this.controller.editorContainer;
    }

    private root?: Root;

    maybeStartTextEditing(shouldPreventTextEditing?: boolean) {
      if (shouldPreventTextEditing || this.canvas.readOnly) {
        return;
      }

      if (this.canvas?.controller.allowSelection() && !this.locked) {
        void this.activeTextField?.startTextEditing(true);
      }
    }

    async startTextEditing(selectAll = false) {
      this.onTextEditingStart?.(selectAll);

      if (!this.controller.props.allowUpdate) {
        return;
      }

      if (!this.hasEmbeddedText()) {
        return;
      }
      if (!this.initialized) {
        await this._instantiateTextObject(this.textContent);
      }
      // Force the object toolbar to redraw to show text tools.
      this.controller.actionShowObjectToolbar();

      if (this.textEditing) {
        return;
      }

      this.textEditing = true;
      this.hasControls = false;
      this.canvas.requestRenderAll();
      this.initialTextContent = this.textContent;
      const position = this.textPosition();

      const editorRect = this.canvas.rectToWindowCoords(position);

      const zoom = this.canvas.getZoom();
      let scale: number;
      if (this.textProps.fontSize === 'fixed') {
        scale = 1;
        editorRect.height = position.height;
        editorRect.width = position.width;
      } else {
        scale = zoom;
        if (this.textProps.fontSize === 'scaleToFit') {
          scale = scale * this.scaleX;
        }
      }

      // Adjust position based on stroke.
      if (this.stroke || this.parent?.stroke) {
        let { strokeWidth } = this;
        if (this.parent) strokeWidth = this.parent.strokeWidth;
        editorRect.width += strokeWidth * zoom;
        editorRect.height += strokeWidth * zoom;
      }

      this.editor = AhaEditorUtils.Editor.createReactEditor();
      this.applyDefaultProperties({ force: true, operateOnAll: true });

      this.root = createRoot(this.containerElement()!);
      this.root.render(
        React.createElement(EditorV2Container, {
          initialHTML: this.textContent,
          editorRect,
          selectAll,
          scale,
          editor: this.editor,
          setText: html => {
            this.set(
              'textContent',
              stripEmptyHTML(this.sanitizeEditorContent(html))
            );
          },
          object: this,
          enterKeyEndsEditing: this.textProps.enterKeyEndsEditing,
        })
      );
    }

    endTextEditing() {
      if (this.textEditing) {
        this.root?.unmount();

        this.textEditing = false;
        this.hasControls = true;
        this.isNewlyCreated = false;

        // Empty text areas are just deleted so they don't pollute the canvas.
        if (this.shapeName === 'text' && this.textContentIsEmpty()) {
          // Put back the initial text content, so that it is there when we undo.
          this.set('textContent', this.initialTextContent);
          this.canvas.remove(this);
          this.deleted(this.controller);
        } else {
          void this.setTextContent(this.textContent, true);

          // Set author id for the first time, in cases where shapes come from
          // templates
          if (this.type === 'shape' && this.authorId == null) {
            this.set('authorId', this.controller.state.userId);
          }

          // unset authorId for empty notes, so that if someone else adds text, they'll become the author
          if (
            this.type === 'shape' &&
            this.authorId &&
            this.textContentIsEmpty()
          ) {
            this.set('authorId', null);
          }
        }
        this.controller.undoManager.completeUndoGroup();
      }
    }

    // I don't completely know why this is necessary. Perhaps to do
    // with the fact that it is XHTML. Without this sanitization entities in
    // code blocks, with HTML formatting enabled, prevent the Image from rendering.
    removeEntities(html: string) {
      // Remove title="&entity;" patterns that are inserted by Prism syntax
      // highlighting.
      return html.replace(/title="&[^;]*;"/g, '');
    }

    textContentAsHTML({
      width,
      height,
      measuring,
      size,
    }: {
      width: string | number;
      height: string | number;
      measuring?: boolean;
      size?: number;
    }) {
      measuring ||= false;

      let hasHeaderStyle = false;
      if (isTextField(this) && isTable(this.parent)) {
        hasHeaderStyle = this.parent.textFieldShouldHaveHeaderStyle(this);
      }

      let textScale = IMAGE_SCALE;

      if (this.canvas?.isHTMLExport()) {
        textScale = 1;

        if (this.textProps.fontSize === 'scaleToFit') {
          // Calculate scaling based on target shape dimensions vs measured text dimensions
          const targetWidth = this.width * this.scaleX;
          const targetHeight = this.height * this.scaleY;
          const textWidth = this.textDimensions?.width || Number(width);
          const textHeight = this.textDimensions?.height || Number(height);

          const scaleX = targetWidth / textWidth;
          const scaleY = targetHeight / textHeight;
          textScale = Math.min(scaleX, scaleY);
        }
      }

      return (
        <div
          // @ts-expect-error TS doesn't like this
          xmlns='http://www.w3.org/1999/xhtml'
          style={{
            minHeight:
              measuring && this.textProps.wrapText !== 'wrapFixed'
                ? undefined
                : Number(height),
            width: width === 'auto' ? 'auto' : Number(width),
            fontSize: size || Number(this.fontSizeWithDefault()),
            boxSizing: 'border-box',
            padding: getPaddingCssFormat(this.scaledTextMargin()),
            transformOrigin: 'top left',
            transform: `scale(${textScale})`,
            textAlign: this.sanitizedTextAlign(),
            justifyContent: verticalAlignToFlex(this.sanitizedVerticalAlign()),
            fontWeight: hasHeaderStyle ? 'bold' : undefined,
          }}
          className={classnames('wb_editor', {
            autoGrow:
              ['growObject', 'user', 'growText'].includes(
                this.textProps.wrapText
              ) && !this.wrapText,
          })}
        >
          <div
            className={classnames('content', this.textClass())}
            dangerouslySetInnerHTML={{
              __html: this.removeEntities(this.sanitizedTextContent()),
            }}
          />
        </div>
      );
    }

    private getTextLocation() {
      const location: string = this.textProps.location;

      if (location === 'user') {
        return this.textLocation || 'above';
      }

      return location;
    }

    // Absolute position where text should be rendered.
    textPosition() {
      const textOffset = 10;
      let { left, top, scaleX, scaleY, strokeWidth } = this;

      // Adjust for non-default text field
      if (this.parent != null) {
        left += this.parent.left;
        top += this.parent.top;
        scaleX = this.parent.scaleX;
        scaleY = this.parent.scaleY;
        strokeWidth = this.parent.strokeWidth;
      }

      let dimensions = this.textDimensions;
      if (!dimensions) {
        // Allow for a placeholder when there is no text content
        // The text is always rendered independent of the scale factor, so we
        // do not need to account for scale here.
        dimensions = {
          width: this.fontSizeWithDefault() * 3,
          height: this.fontSizeWithDefault(),
        };
      }

      const location = this.getTextLocation();

      switch (location) {
        case 'inside':
          if (
            ['wrap', 'wrapGrow', 'wrapFixed'].includes(this.textProps.wrapText)
          ) {
            return {
              left: left + strokeWidth / 2,
              top: top + strokeWidth / 2,
              width: this.width * scaleX - strokeWidth,
              height: this.height * scaleY - strokeWidth,
            };
          } else {
            return {
              left,
              top: Math.max(
                top,
                top + (this.height * scaleY - dimensions.height) / 2
              ),
              width: dimensions.width,
              height: dimensions.height,
            };
          }

        case 'above': {
          let zoomAdjustment = 1;
          if (this.textProps.fontSize === 'fixed') {
            zoomAdjustment = this.canvas?.getZoom() || 1;
          }

          switch (this.sanitizedTextAlign()) {
            case 'center':
              return {
                left: left + (this.width * scaleX) / 2 - dimensions.width / 2,
                top: top - dimensions.height / zoomAdjustment,
                width: dimensions.width,
                height: dimensions.height,
              };
            default:
              return {
                left,
                top: top - dimensions.height / zoomAdjustment,
                width: dimensions.width,
                height: dimensions.height,
              };
          }
        }

        case 'below':
          switch (this.sanitizedTextAlign()) {
            case 'center':
              return {
                left: left + (this.width * scaleX) / 2 - dimensions.width / 2,
                top: top + this.height * scaleY,
                width: dimensions.width,
                height: dimensions.height,
              };
            default:
              return {
                left,
                top: top + this.height * scaleY,
                width: dimensions.width,
                height: dimensions.height,
              };
          }

        case 'left':
          return {
            left: left - (dimensions.width + textOffset),
            top: top + (this.height * scaleY) / 2 - dimensions.height / 2,
            width: dimensions.width,
            height: dimensions.height,
          };

        case 'right':
          return {
            left: left + this.width * scaleX + textOffset,
            top: top + (this.height * scaleY) / 2 - dimensions.height / 2,
            width: dimensions.width,
            height: dimensions.height,
          };

        default:
          throw new Error(`Unknown text location: ${location}`);
      }
    }

    // Only works for shapes that have scaleToFit set. This is used primarily
    // when dynamically adjusting the margins of a shape (e.g., ShapeNote as you
    // add ReactionActions).
    async forceResizeTextToFit() {
      this.clearDimensionsCache();
      await this.sizeTextToFit();
      await this.renderTextToImage();
    }

    // For 'wrapFixed' text, we need to ensure that the textDimensions match the
    // new scaled sized.
    private async forceRemeasureText() {
      if (this.textProps.wrapText !== 'wrapFixed') {
        throw new Error(
          'forceRemasureText should only be called for wrapFixed'
        );
      }
      this.clearDimensionsCache();
      await this.renderTextToImage();
    }

    private getImageDimensions() {
      const position = this.textPosition();

      if (this.textProps.fontSize === 'scaleToFit') {
        return {
          width: position.width / this.scaleX,
          height: position.height / this.scaleY,
        };
      }

      return {
        width: position.width,
        height: position.height,
      };
    }

    public async renderTextToImage() {
      if (!this.textContent || this.textContent.length === 0) return null;

      this.textDimensions = await this.measureTextDimensions();

      const { width, height } = this.getImageDimensions();

      const foreignObjectHeight = '100%';

      const svgWrappedHtml = ReactDOMServer.renderToString(
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width={width * IMAGE_SCALE}
          height={height * IMAGE_SCALE}
        >
          <style>{renderedTextStyles}</style>
          <foreignObject width='100%' height={foreignObjectHeight}>
            {this.textContentAsHTML({ width, height })}
          </foreignObject>
        </svg>
      );

      const image = new Image();
      image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
        svgWrappedHtml
      )}`;

      // Uncomment the next line to debug styling differences between the editing and
      // non-editing versions of the HTML. Most problems are likely to be
      // because this SVG *only* gets CSS that is in editorStyles.module.css
      // and does not inherit any other CSS from the page.
      // this.debugForeignObjectContent(svgWrappedHtml);

      image
        .decode()
        .then(() => {
          if (this.textContent != null) {
            this.textImage = image;
          }
          this.controller.renderManager.refreshObject(this);
          this.canvas?.requestRenderAll();
        })
        .catch(error => {
          console.error(`Failed to render text`, error);
        });

      this.determineLinkPositionsDebounced();
    }

    determineLinkPositionsDebounced() {
      if (this.determineLinkPositionsTimeout) {
        clearTimeout(this.determineLinkPositionsTimeout);
        this.determineLinkPositionsTimeout = undefined;
      }

      this.determineLinkPositionsTimeout = setTimeout(() => {
        this.determineLinkPositions();
        this.determineLinkPositionsTimeout = undefined;
      }, 500);
    }

    // work out positions of links in text content and save in this.linkData
    determineLinkPositions() {
      const { width, height } = this.getImageDimensions();
      // Text fields have a relative offset to their parent, so we need to adjust
      // the client rect by that offset
      const { left: leftOffset, top: topOffset } = this.parent
        ? this
        : { left: 0, top: 0 };

      const measurementDoc = getMeasurementDoc(
        ReactDOMServer.renderToString(
          <>
            <style>{renderedTextStyles}</style>
            {this.textContentAsHTML({ width, height })}
          </>
        )
      );

      if (!measurementDoc) {
        console.warn('Could not create measurement doc');
        return;
      }

      this.linkData = [];
      const imageScale = IMAGE_SCALE;

      for (const el of measurementDoc.querySelectorAll('a')) {
        const href = el.getAttribute('href');

        if (href) {
          const rects = [...el.getClientRects()].map(rect => ({
            top: rect.top / imageScale - topOffset,
            left: rect.left / imageScale - leftOffset,
            width: rect.width / imageScale,
            height: rect.height / imageScale,
          }));

          this.linkData.push({
            href,
            rects,
          });
        }
      }
    }

    debugForeignObjectContent(svgWrappedHtml: string) {
      if (!this.debugContainer) {
        this.debugContainer = document.createElement('div');
        this.debugContainer.style.cssText =
          'position: absolute; top: 0; border: 1px solid red; transform: scale(0.5)';
        document.body.appendChild(this.debugContainer);
      }
      this.debugContainer.innerHTML = svgWrappedHtml;
    }

    guardTextOpacity() {
      // Prevent null opacity. Fabric treats undefined as 1, but null as 0
      if (!this.textOpacity) {
        return 1;
      }
      // Prevent invisible text. 0.1 is the lowest we allow a user to set
      // opacity via the opacity slider
      if (this.textOpacity <= 0) {
        return 0.1;
      }
      // Set upper bound for text opacity
      if (this.textOpacity > 1) {
        return 1;
      }
      return this.textOpacity;
    }

    private relativeTextPosition() {
      const position = this.textPosition();

      // Adjust for non-default text field
      const { left, top } = this.parent ?? this;

      return {
        ...position,
        top: position.top - top,
        left: position.left - left,
      };
    }

    renderExternalContent() {
      // We want to render any external content this shape might have, as well as
      // the text as external content.
      //
      // Note: This method will only be called for text when exporting to HTML
      const externalContent = super.renderExternalContent();

      // If we have text content, render both positioned absolutely (over each other)
      // with the text on top (last)
      if (this.textContent && this.textContent.length) {
        if (!this.textDimensions) {
          this.textDimensions = this.measureTextDimensions(true);
        }
        const { width, height } = this.getImageDimensions();

        return (
          <>
            <div style={{ position: 'absolute' }}>{externalContent}</div>
            <div
              style={{
                position: 'absolute',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              {this.textContentAsHTML({ width, height })}
            </div>
          </>
        );
      }

      return externalContent;
    }

    renderText(ctx: CanvasRenderingContext2D, onlyFields = false) {
      if (this.canvas.isHTMLExport() && this.hasExternalContent()) {
        return;
      }

      // Render text for any text fields on this object
      Object.values(this._textFields)?.forEach(field => field.renderText(ctx));

      if (onlyFields) {
        return;
      }

      if (!this.textEditing) {
        const image = this.textImage;
        const imageScale = IMAGE_SCALE;

        if (!image || !image.complete || image.naturalHeight === 0) {
          return;
        }

        const position = this.relativeTextPosition();

        // Adjust for non-default text field
        const { width, height, scaleX, scaleY } = this.parent ?? this;

        let x = imageScale * ((-width * scaleX) / 2 + position.left);
        let y = imageScale * ((-height * scaleY) / 2 + position.top);
        let imageWidth = (position.width * imageScale) / scaleX;
        let imageHeight = (position.height * imageScale) / scaleY;

        ctx.save();
        ctx.globalAlpha = this.guardTextOpacity();

        // Reset the zoom in the transform
        if (this.textProps.fontSize === 'scaleToFit') {
          ctx.scale(1 / (imageScale * scaleX), 1 / (imageScale * scaleY));
          imageWidth *= scaleX;
          imageHeight *= scaleY;
        } else {
          ctx.scale(1 / imageScale, 1 / imageScale);
          x /= scaleX;
          y /= scaleY;
        }

        ctx.drawImage(image, x, y, imageWidth, imageHeight);
        ctx.restore();
      }
    }

    sanitizeEditorContent(html: string) {
      if (this.textProps.allowedStyles === 'simple') {
        return simpleSanitizer(html);
      } else {
        return regularSanitizer(html);
      }
    }

    // Sanitize the raw HTML to ensure it doesn't contain any XSS or tags we
    // don't support. This is expensive, and we use this text a lot, so we
    // memoize the sanitized content. Use sanitizedTextContent to access
    // the memoized + sanitized text content.
    sanitizeTextContent(): string {
      this._sanitizedTextContent = this.sanitizeEditorContent(this.textContent);
      return this._sanitizedTextContent ?? '';
    }

    sanitizedTextContent(): string {
      return this._sanitizedTextContent || this.sanitizeTextContent();
    }

    sanitizedTextAlign() {
      let align: EditorAlign;

      if (this.textProps.textAlign === 'user') {
        align = this.activeTextField?.textAlign ?? this.textAlign;
      } else if (this.textProps.textAlign === 'shape') {
        align = this.shape.shapeTextAlign();
      } else {
        align = this.textProps.textAlign;
      }

      if (!['left', 'center', 'right'].includes(align)) align = 'center';

      return align;
    }

    sanitizedVerticalAlign() {
      let align: string;

      if (this.textProps.verticalAlign === 'user') {
        align = this.verticalAlign;
      } else if (this.textProps.verticalAlign === 'shape') {
        align = this.shape.shapeVerticalAlign();
      } else {
        align = this.textProps.verticalAlign;
      }

      // Normalize any legacy values (flex-start, flex-end, center) to standard (top, middle, bottom)
      return normalizeVerticalAlign(align);
    }

    private clearDimensionsCache() {
      this.dimensionsCache = {};
    }

    private dimensionsKey(size?: number) {
      return [
        this.textContent,
        size ?? this.fontSize,
        this.width,
        this.textLocation,
      ]
        .map(String)
        .join();
    }

    private dimensionsCache: Record<string, TextDimensions>;
    measureTextDimensions(sync?: false, size?: number): Promise<TextDimensions>;
    measureTextDimensions(sync: true, size?: number): TextDimensions;

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    measureTextDimensions(
      sync?: boolean,
      size?: number
    ): TextDimensions | Promise<TextDimensions> {
      if (!this.dimensionsCache) {
        this.dimensionsCache = {};
      }

      // Measuring the text is extremely expensive, so we cache the value.
      const cacheKey = this.dimensionsKey(size);
      const cached = this.dimensionsCache[cacheKey];
      if (cached) {
        return cached;
      }

      const saveValue = (raw: TextDimensions) => {
        const rounded = {
          width: Math.round(raw.width),
          height: Math.round(raw.height),
        };
        this.dimensionsCache[cacheKey] = rounded;
        return rounded;
      };

      if (!this.textContent || this.textContent.length === 0) {
        return saveValue({
          width: AUTO_GROW_MIN_WIDTH,
          height: this.fontSizeWithDefault(),
        });
      }

      const position = this.textPosition();

      let width: string | number = position.width;
      let height = position.height;
      if (this.textProps.fontSize === 'scaleToFit') {
        width /= this.scaleX;
        height /= this.scaleY;
      }

      if (['growObject', 'growText'].includes(this.textProps.wrapText)) {
        width = 'auto';
      } else if (this.textProps.wrapText === 'user') {
        if (this.wrapText) {
          width = this.width * this.scaleX;
          height = this.height * this.scaleY;
        } else {
          width = 'auto';
        }
      }

      this.cancelCleanupResizedElement();
      let element: HTMLElement;
      if (!this.resizeObservedElement) {
        element = document.createElement('div');
        element.style.visibility = 'hidden';
        element.style.display = 'inline-block';
        // To prevent <body> `display: flex` from making this item take 100% of the window.
        element.style.alignSelf = 'flex-start';
        // move element to area where it won't show. visibility: hidden
        // still leaves whitespace, so this moves it to an area where it
        // does not show in the app
        element.style.position = 'absolute';
        document.body.appendChild(element);
        this.resizeObservedElement = element;
      } else {
        element = this.resizeObservedElement;
      }
      element.innerHTML = ReactDOMServer.renderToString(
        this.textContentAsHTML({ width, height, size, measuring: true })
      );

      // only do more expensive (reflow inducing) measurement when live
      // editing to ensure smoothness
      // round the result, to match the values we get from clientWidth/clientHeight in
      // the EditorV2Container component which mutates this object
      if (sync) {
        const boundingRect = element.getBoundingClientRect();
        // boundingRect does not include the transform(scale)
        const elementWidth = boundingRect.width;
        const elementHeight = boundingRect.height;
        this.delayedCleanupResizedElement();
        return saveValue({
          width: elementWidth,
          height: elementHeight,
        });
      } else {
        return new Promise<TextDimensions>(resolve => {
          this.addObservedElement(element, (el, bounds) => {
            const { width, height } = bounds;
            this.observer?.unobserve(el);
            this.delayedCleanupResizedElement();
            resolve(saveValue({ width, height }));
          });
        });
      }
    }

    // Adjust the size of the object to contain the text.
    growObjectToFit(widthChange = false) {
      if (this.canvas && !this.textEditing) {
        const { height, width } = this.measureTextDimensions(true);

        if (height && !this.textContentIsEmpty()) {
          if (
            this.textProps.wrapText === 'wrapGrow' &&
            height < this.minHeight
          ) {
            return;
          }
          this.set('height', height / this.scaleY);
        }

        // Update the width of the object to fit the text if the user has not
        // set a fixed width. Don't do this for classes that have text fields, otherwise
        // it can cause problems when (eg) adding a column to a table or resizing a table.
        if (!this.hasTextFields && !this.wrapText && !widthChange) {
          this.set('width', width / this.scaleX);
        }

        this.setCoords();
        this.syncTextImage();
      }
    }

    // Debounce this function, as measuring text is expensive
    async sizeTextToFit() {
      if (this.textProps.fontSize !== 'scaleToFit') {
        throw new Error(
          'sizeTextToFit should only be called for objects with scaleToFit'
        );
      }

      if (this.isDebouncing) {
        // We need to wait for the await below to finish so that the font
        // size is actually set before we resolve.
        await new Promise(resolve => {
          const checkDebouncing = () => {
            if (!this.isDebouncing) {
              resolve(null);
            } else {
              setTimeout(checkDebouncing, 10);
            }
          };
          checkDebouncing();
        });
      }

      this.isDebouncing = true;
      this.fontSize = await this.shrinkText();
      this.isDebouncing = false;
    }

    // Scale the text so that it fits within the note. We need a heuristic to
    // start with a smaller font size if the text contains very long words.
    // Otherwise a single long word will be broken onto multiple lines.
    // Note that size is 2x the px size because of scaling.
    async shrinkText() {
      const targetHeight = this.height;
      let iterations = 0;
      let size = DEFAULT_FONT_SIZE;

      // Make sure the text cache is up to date.
      this.sanitizeTextContent();

      while (true) {
        const { height } = await this.measureTextDimensions(false, size);
        if (height > targetHeight) {
          if (size <= 3) {
            return 3;
          }

          size = Math.floor(size - size / 10);
        } else {
          break;
        }

        if (iterations++ > 10) break;
      }
      return size;
    }

    scaledTextMargin() {
      const margin = this.calcTextMargin();
      if (this.textProps.margins === 'noScale') {
        return margin;
      } else {
        const scale = Math.min(this.scaleX, this.scaleY);

        return {
          top: margin.top * scale,
          left: margin.left * scale,
          right: margin.right * scale,
          bottom: margin.bottom * scale,
        };
      }
    }

    // Compute a new left value based on a change in width, and the alignment.
    // Also updates top if the shape is rotated.
    growText(widthDiff: number) {
      const rads = fabric.util.degreesToRadians(this.angle!);
      let x = 0;
      let y = 0;

      switch (this.textAlign) {
        case 'left':
          return { top: this.top, left: this.left };

        case 'right':
          x = Math.cos(rads) * widthDiff;
          y = Math.sin(rads) * widthDiff;
          return { top: this.top - y, left: this.left - x };

        case 'center':
        default:
          x = (Math.cos(rads) * widthDiff) / 2;
          y = (Math.sin(rads) * widthDiff) / 2;
          return { top: this.top - y, left: this.left - x };
      }
    }

    // Reset the scale to track the font-size. We also adjust the left
    // side so that the alignment is respected.
    updateFontSize() {
      this.withoutRecusion(() => {
        const newScale = this.fontSize! / DEFAULT_FONT_SIZE;
        this.set({
          ...this.growText(this.width * newScale - this.width * this.scaleX),
          scaleX: newScale,
          scaleY: newScale,
        });

        if (this.shapeName === 'text') {
          this.growObjectToFit();
        }
      });
      this.synchronize();
    }

    createOffscreenEditor(): AhaSlateEditor {
      const editor = createEditor();
      editor.children = new HtmlSerializer(undefined, {
        preserveWhitespace: true,
        enableFontFamilyMark: true,
      }).deserialize(this.textContent).children;

      AhaEditorUtils.Selection.selectAll(editor);

      return editor;
    }

    // Modify the text by calling `applyFunc`, passing in the editor instance
    // to apply commands to. Passing true to `operateOnAll` will apply the
    // function to all of the text, regardless of the current selection.
    applyTextProperty(
      applyFunc: (editor: AhaSlateEditor, obj: fabric.Object) => void,
      operateOnAll = false
    ) {
      const editor = this.getCurrentEditor();
      if (this.textEditing) {
        const selection = editor.selection;
        // TODO: Maybe handle all this in AhaEditorUtils?
        // Something like AhaEditorUtils.Selection.preserveSelection(applySelection)
        // may be overkill
        if (operateOnAll) {
          AhaEditorUtils.Selection.selectAll(editor);
        }

        applyFunc(editor, this);

        if (operateOnAll) {
          // Reset selection
          AhaEditorUtils.Selection.select(editor, selection);
        }
      } else if (this.hasTextFields) {
        this.selectedTextFields.forEach(field => {
          field.applyTextProperty(applyFunc, operateOnAll);
        });
      } else {
        // Apply the change.
        applyFunc(editor, this);
        // Save the updated text again.
        const html = new HtmlSerializer(editor).serialize();
        this.set('textContent', stripEmptyHTML(html));
      }
    }

    // Apply properties that are applied via AhaEditor
    applyDefaultProperties({ force = false, operateOnAll = false } = {}) {
      if (force || this.textContent === this.defaultContent()) {
        TEXT_MARKS.forEach(mark => {
          if (this[mark]) {
            this.applyTextProperty(
              editor => AhaEditorUtils.Marks.add(editor, mark, this[mark]),
              operateOnAll
            );
          }
        });

        if (this.textColor) {
          this.applyTextProperty(editor => {
            AhaEditorUtils.Selection.selectAll(editor);
            applyTextColor(this, this.textColor, operateOnAll);
          });
        }
      }
    }

    // Read a property from the current text by calling `readFunc`, passing in
    // the editor instance to apply queries to.
    readTextProperty<T>(
      readFunc: (editor: AhaSlateEditor, obj: fabric.Object) => T
    ): T {
      const editor = this.getCurrentEditor();
      return readFunc(editor, this);
    }

    getCurrentEditor(): AhaSlateEditor {
      if (this.textEditing) {
        if (!this.editor) {
          throw new Error('this.editor is not set while textEditing = true');
        }
        return this.editor;
      }
      return this.createOffscreenEditor();
    }

    // Return plain text for the HTML.
    plainText() {
      return plainText(this.sanitizedTextContent());
    }

    textContentToJSON() {
      return this.textContent || '';
    }

    addObservedElement(
      element: Element,
      callback: (el: Element, bounds: Rect) => void
    ) {
      if (!this._observedElements) {
        // Ideally there would be one instance of ResizeObserver per
        // canvas, but canvas is not always available at the time we need to
        // measure text
        this._observedElements = new Map();
        this.observer = new ResizeObserver(entries => {
          for (const entry of entries) {
            const callback = this._observedElements!.get(entry.target);
            const { inlineSize: width, blockSize: height } =
              entry.contentBoxSize[0];

            if (width === 0) continue;
            callback?.(entry.target, { width, height });
          }
        });
      }

      this._observedElements.set(element, callback);
      this.observer!.observe(element);
    }

    // Clean up observer, which will unobserve all observed elements
    removeObserver() {
      this.observer?.disconnect();
      this.observer = null;
      this._observedElements = null;
    }

    textTools(): ToolList {
      if (this.shape?.textTools) {
        return this.shape.textTools();
      } else {
        let t: ToolList = [];
        const activeTextField = this.activeTextField ?? this;

        if (
          (activeTextField === this && this.hasTextFields) || // this line needs to be more choosy
          activeTextField.textContent?.length > 0 ||
          activeTextField.textEditing
        ) {
          t = t.concat([
            [tools.ToolTextColor, tools.ToolTextFormat, tools.ToolTextList],
          ]);
          if (this.shapeName !== 'note') {
            t = t.concat([tools.ToolTextAlign]);
          }
          if (this.textProps.fontSize !== 'scaleToFit') {
            t = t.concat([tools.ToolFontSize]);
          }
        }
        return t;
      }
    }

    /**
     * After a small delay removes the element from the DOM
     * Used instead of removing immediately to avoid performance issues when resizing text objects.
     */
    delayedCleanupResizedElement() {
      if (this.cleanupResizedElementTimer) {
        return;
      }
      this.cleanupResizedElementTimer = setTimeout(() => {
        this.cleanupResizedElementTimer = undefined;
        if (!this.resizeObservedElement) {
          return;
        }
        this.resizeObservedElement.remove();
        this.resizeObservedElement = undefined;
      }, CLEANUP_DELAY);
    }

    cancelCleanupResizedElement() {
      if (!this.cleanupResizedElementTimer) {
        return;
      }

      clearTimeout(this.cleanupResizedElementTimer);
      this.cleanupResizedElementTimer = undefined;
    }
  };
}

export {
  type EmbeddedTextBehaviors,
  IMAGE_SCALE,
  DEFAULT_FONT_SIZE,
  type TextProps,
};

export default EmbeddedTextBehaviors;
